import { memo, useCallback, useEffect } from 'react';
import { Pressable } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';

import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { SvgIcon } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import type { CalendarViewMode } from './screen-utils';

const strings = STRINGS.guardian.kids;

const SEGMENT = 36;
/** Mirrors the app tab bar's indicator spring (`src/components/ui/tabbar`). */
const SPRING_CONFIG = { damping: 30, stiffness: 300, mass: 0.8 };
const ACTIVE_ICON_COLOR = colors['0'];
const INACTIVE_ICON_COLOR = '#141B34';

const SEGMENTS: { mode: CalendarViewMode; icon: AvailableIconName; label: string }[] = [
  { mode: 'calendar', icon: 'IcCalendar1', label: strings.calendarViewLabel },
  { mode: 'list', icon: 'IcList', label: strings.listViewLabel },
];

export type ViewToggleProps = {
  mode: CalendarViewMode;
  onChangeMode: (mode: CalendarViewMode) => void;
};

/**
 * Calendar ↔ List segmented control: a white, shadowed pill (same surface as the filter
 * chips) with a navy indicator that springs between the two icons on change.
 */
function ViewToggle({ mode, onChangeMode }: ViewToggleProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const activeIndex = Math.max(
    0,
    SEGMENTS.findIndex(segment => segment.mode === mode),
  );
  const distance = scale(SEGMENT);
  const progress = useSharedValue(activeIndex);

  useEffect(() => {
    progress.value = withSpring(activeIndex, SPRING_CONFIG);
  }, [activeIndex, progress]);

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: progress.value * distance }],
  }));

  return (
    <Stack
      direction="row"
      borderRadius={scale(14)}
      backgroundColor={colors['0']}
      style={styles.track}>
      <Animated.View style={[styles.indicator, indicatorStyle]} />
      {SEGMENTS.map(segment => (
        <SegmentButton
          key={segment.mode}
          icon={segment.icon}
          label={segment.label}
          isActive={segment.mode === mode}
          mode={segment.mode}
          onPress={onChangeMode}
        />
      ))}
    </Stack>
  );
}

type SegmentButtonProps = {
  icon: AvailableIconName;
  label: string;
  isActive: boolean;
  mode: CalendarViewMode;
  onPress: (mode: CalendarViewMode) => void;
};

const SegmentButton = memo(({ icon, label, isActive, mode, onPress }: SegmentButtonProps) => {
  const { scale } = useScaling();
  const styles = useStyles();
  const handlePress = useCallback(() => onPress(mode), [mode, onPress]);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      accessibilityLabel={label}
      onPress={handlePress}
      style={styles.segment}>
      <SvgIcon
        name={icon}
        size={scale(20)}
        color={isActive ? ACTIVE_ICON_COLOR : INACTIVE_ICON_COLOR}
      />
    </Pressable>
  );
});
SegmentButton.displayName = 'ViewToggleSegment';

const useStyles = createStyles(({ scale }) => ({
  track: {
    shadowColor: '#0F225B',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 4,
  },
  indicator: {
    position: 'absolute',
    width: scale(SEGMENT),
    height: scale(SEGMENT),
    borderRadius: scale(10),
    backgroundColor: guardianColors['Main/Main'],
  },
  segment: {
    width: scale(SEGMENT),
    height: scale(SEGMENT),
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default memo(ViewToggle);
